import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { TIMELINE_CONFIG } from '../constants';
import {
  clamp,
  getTimelineWidth,
  timeToPixel,
} from '../utils/time-calculations';

export const useAdvancedTimelineLayout = ({
  outputDurationSec,
  zoom,
  outputTimeSec,
}: {
  outputDurationSec: number;
  zoom: number;
  outputTimeSec: number;
}) => {
  const timelineViewportRef = useRef<HTMLDivElement | null>(null);
  const [timelineViewportWidthPx, setTimelineViewportWidthPx] = useState(0);

  useLayoutEffect(() => {
    const viewport = timelineViewportRef.current;
    if (!viewport) {
      return;
    }

    let animationFrameId: number | null = null;

    const commitViewportWidth = () => {
      const nextWidth = viewport.clientWidth;
      setTimelineViewportWidthPx(previousWidth =>
        previousWidth === nextWidth ? previousWidth : nextWidth,
      );
    };

    const scheduleViewportWidthUpdate = () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        commitViewportWidth();
      });
    };

    scheduleViewportWidthUpdate();

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (animationFrameId != null) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleViewportWidthUpdate();
    });
    resizeObserver.observe(viewport);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  const pixelsPerSecond = useMemo(() => {
    if (outputDurationSec <= 0 || timelineViewportWidthPx <= 0) {
      return TIMELINE_CONFIG.pixelsPerSecond;
    }
    return timelineViewportWidthPx / outputDurationSec;
  }, [outputDurationSec, timelineViewportWidthPx]);

  const timelineWidthPx = useMemo(() => {
    if (outputDurationSec <= 0) {
      return Math.max(1, timelineViewportWidthPx);
    }
    if (timelineViewportWidthPx <= 0) {
      return getTimelineWidth(
        outputDurationSec,
        TIMELINE_CONFIG.pixelsPerSecond,
        zoom,
      );
    }
    return timelineViewportWidthPx * zoom;
  }, [outputDurationSec, timelineViewportWidthPx, zoom]);

  const playbackPlayheadPositionPx = useMemo(
    () =>
      clamp(
        timeToPixel(outputTimeSec, pixelsPerSecond, zoom),
        0,
        Math.max(0, timelineWidthPx - 1),
      ),
    [outputTimeSec, pixelsPerSecond, timelineWidthPx, zoom],
  );

  return {
    timelineViewportRef,
    pixelsPerSecond,
    timelineWidthPx,
    playbackPlayheadPositionPx,
  };
};
